import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Icon from '@amzn/awsui-components-console/icon';
import InfoTooltip from 'src/common/ui/InfoTooltip';
import { useDarkMode } from 'src/common/ui/DarkModeContext';
import { getUsernameFromJwt } from 'src/common/utils/auth';
import {
  BADGE_PHOTO_URL,
  HELP_URL,
  FEEDBACK_FORM_URL,
  CUT_TICKET_URL,
} from 'src/common/constants';
import swooshUrl from 'src/images/langhub-swoosh.svg';
import langhubLogo from 'src/images/langhub-logo.png';
import styles from './AppHeader.module.scss';
import { HOME_PATH } from 'src/common/routes';
import { recordRumPageView } from 'src/common/rum/rum';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import { SUPPORTED_LOCALES } from 'src/i18n/locales';

export interface AppHeaderProps {
  /** Optional content to render between the logo and the utilities (nav links, breadcrumbs, etc.) */
  navContent?: React.ReactNode;
  /** When true, hides the dark mode toggle button from the utility bar */
  hideDarkModeToggle?: boolean;
}

interface DarkModeToggleProps {
  readonly isDarkMode: boolean;
  readonly onToggle: () => void;
  /**
   * Forwarded to the real `aria-describedby` attribute. `InfoTooltip`'s wrapper
   * mode clones its child with this Cloudscape-convention camelCase prop, which
   * a plain <button> would otherwise receive as an unrecognized DOM attribute.
   */
  readonly ariaDescribedby?: string;
}

const DarkModeToggle: React.FC<DarkModeToggleProps> = ({
  isDarkMode,
  onToggle,
  ariaDescribedby,
}) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className={styles.utilSm}
      aria-label={
        isDarkMode
          ? t('app-header-switch-to-light')
          : t('app-header-switch-to-dark')
      }
      aria-describedby={ariaDescribedby}
      onClick={onToggle}
      data-testid="dark-mode-toggle"
    >
      <Icon name="light-dark" />
    </button>
  );
};

const FEEDBACK_OPTIONS = [
  { labelId: 'app-header-feedback-share', href: FEEDBACK_FORM_URL },
  { labelId: 'app-header-feedback-ticket', href: CUT_TICKET_URL },
] as const;

/** Header Feedback toggle that expands to the feedback form and ticket links. */
const FeedbackMenu: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className={styles.menuContainer} ref={containerRef}>
      <button
        type="button"
        className={styles.utilSm}
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((prev) => !prev)}
      >
        {t('app-header-feedback')}
        <Icon name={open ? 'caret-up-filled' : 'caret-down-filled'} />
      </button>
      {open && (
        <ul id={menuId} className={styles.menu}>
          {FEEDBACK_OPTIONS.map(({ labelId, href }) => (
            <li key={labelId}>
              <a
                className={styles.menuItem}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
              >
                {t(labelId)}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AppHeader: React.FC<AppHeaderProps> = ({
  navContent,
  hideDarkModeToggle,
}) => {
  const username = getUsernameFromJwt();
  const [imgFailed, setImgFailed] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { t, locale, setLocale } = useTranslation();
  const navigate = useNavigate();

  /**
   * Route to /home in-app instead of letting the browser follow the href, which
   * would reload the whole document (re-downloading the bundle, discarding the
   * Apollo cache, and re-running the Midway session check). The href is kept so
   * the logo stays a real link for accessibility, and modified clicks
   * (new tab/window, middle click) fall through to the browser unchanged.
   */
  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    navigate(HOME_PATH);
  };

  return (
    <header className={`${styles.bar} langhub-app-header`}>
      {/* Left cluster: identity + optional nav content */}
      <div className={styles.left}>
        <a
          href={HOME_PATH}
          className={styles.identity}
          aria-label={t('app-header-home-aria')}
          onClick={handleLogoClick}
        >
          <img src={langhubLogo} height={25} alt="" />
          <span className={styles.wordmark}>
            <span className={styles.title}>LangHub</span>
            <img
              src={swooshUrl}
              alt=""
              aria-hidden="true"
              width={80}
              height={15}
              className={styles.swoosh}
            />
          </span>
        </a>
        {navContent}
      </div>

      {/* Right cluster: utilities */}
      <div className={styles.utilities}>
        {!hideDarkModeToggle && (
          /* `position="bottom"` because the header is sticky at the top of the
             viewport — a tooltip above the trigger would be clipped off-screen. */
          <InfoTooltip
            content={t('app-header-view-mode-tooltip')}
            position="bottom"
          >
            <DarkModeToggle isDarkMode={isDarkMode} onToggle={toggleDarkMode} />
          </InfoTooltip>
        )}

        <a
          className={styles.utilSm}
          href={HELP_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('app-header-help-aria')}
          // Help routes to an external page (new tab), so there is no react-router navigation for
          // usePageViewMetrics to observe. Emit the Help page view explicitly on click so it is
          // counted alongside the in-app pages.
          onClick={() => recordRumPageView('Help')}
        >
          {t('app-header-help')}
        </a>

        <FeedbackMenu />

        <select
          className={styles.langSelect}
          aria-label={t('app-header-language-label')}
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
        >
          {SUPPORTED_LOCALES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={`${styles.utilSm} ${styles.userBtn}`}
          aria-label={t('app-header-user-menu-aria')}
        >
          {username && !imgFailed ? (
            <img
              src={`${BADGE_PHOTO_URL}${username}`}
              alt=""
              aria-hidden="true"
              className={styles.avatar}
              onError={() => setImgFailed(true)}
            />
          ) : (
            <span className={styles.avatarFallback} aria-hidden="true">
              {username ? username.charAt(0).toUpperCase() : ''}
            </span>
          )}
        </button>
      </div>
    </header>
  );
};

export default AppHeader;
